import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="lightbox"
// Full-size photo lightbox for the event gallery strip. Tapping a thumbnail
// opens an overlay at that photo with prev/next (arrows, keyboard, swipe) and a
// "2 / 5" counter. Photo URLs come from the thumb tiles plus any `extra`
// <template>s (photos beyond the 4 shown inline).
export default class extends Controller {
  static targets = ["thumb", "extra"]

  connect() {
    // Full set of photo URLs, in order: visible thumbs then hidden extras.
    this.urls = [
      ...this.thumbTargets.map((t) => t.dataset.full),
      ...this.extraTargets.map((t) => t.dataset.full),
    ]
    this.index = 0
    this._onKey = this.onKey.bind(this)
  }

  open(event) {
    this.index = Number(event.currentTarget.dataset.index) || 0
    this.render()
    document.addEventListener("keydown", this._onKey)
    document.body.style.overflow = "hidden"
  }

  close() {
    this.overlay?.remove()
    this.overlay = null
    document.removeEventListener("keydown", this._onKey)
    document.body.style.overflow = ""
  }

  next() { this.index = (this.index + 1) % this.urls.length; this.update() }
  prev() { this.index = (this.index - 1 + this.urls.length) % this.urls.length; this.update() }

  onKey(e) {
    if (e.key === "Escape") this.close()
    else if (e.key === "ArrowRight") this.next()
    else if (e.key === "ArrowLeft") this.prev()
  }

  render() {
    this.overlay = document.createElement("div")
    this.overlay.className = "lightbox-overlay"
    const multi = this.urls.length > 1
    this.overlay.innerHTML = `
      <button type="button" class="lightbox-close" aria-label="Close">&times;</button>
      ${multi ? '<button type="button" class="lightbox-nav lightbox-prev" aria-label="Previous">&#8249;</button>' : ""}
      <figure class="lightbox-figure"><img class="lightbox-img" alt=""></figure>
      ${multi ? '<button type="button" class="lightbox-nav lightbox-next" aria-label="Next">&#8250;</button>' : ""}
      ${multi ? '<div class="lightbox-counter"></div>' : ""}
      ${multi ? `<div class="lightbox-dots">${this.urls.map((_, i) => `<button type="button" class="lightbox-dot" data-i="${i}" aria-label="Photo ${i + 1}"></button>`).join("")}</div>` : ""}`

    this.overlay.querySelector(".lightbox-close").addEventListener("click", () => this.close())
    this.overlay.addEventListener("click", (e) => { if (e.target === this.overlay) this.close() })
    if (multi) {
      this.overlay.querySelector(".lightbox-next").addEventListener("click", () => this.next())
      this.overlay.querySelector(".lightbox-prev").addEventListener("click", () => this.prev())
      this.addSwipe(this.overlay.querySelector(".lightbox-figure"))
      // Dot indicators (mobile nav — chevrons are hidden there): tap to jump.
      this.overlay.querySelectorAll(".lightbox-dot").forEach((dot) => {
        dot.addEventListener("click", () => { this.index = Number(dot.dataset.i); this.update() })
      })
    }
    document.body.appendChild(this.overlay)
    this.update()
  }

  update() {
    if (!this.overlay) return
    this.overlay.querySelector(".lightbox-img").src = this.urls[this.index]
    const counter = this.overlay.querySelector(".lightbox-counter")
    if (counter) counter.textContent = `${this.index + 1} / ${this.urls.length}`
    this.overlay.querySelectorAll(".lightbox-dot").forEach((dot, i) => {
      dot.classList.toggle("is-active", i === this.index)
    })
  }

  // Basic horizontal swipe for touch: >40px flick moves prev/next.
  addSwipe(el) {
    let startX = null
    el.addEventListener("touchstart", (e) => { startX = e.touches[0].clientX }, { passive: true })
    el.addEventListener("touchend", (e) => {
      if (startX === null) return
      const dx = e.changedTouches[0].clientX - startX
      if (dx > 40) this.prev()
      else if (dx < -40) this.next()
      startX = null
    }, { passive: true })
  }

  disconnect() { this.close() }
}
